import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { IoCalculator, IoArrowForward, IoCash, IoTrendingUp, IoWallet, IoStatsChart, IoGridOutline, IoBarChartOutline, IoListOutline, IoSaveOutline, IoDownloadOutline } from 'react-icons/io5';
import { exportManualModeResults } from '../utils/exportToExcel';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import AccordionSection from '../components/shared/AccordionSection';
import InputGroup from '../components/shared/InputGroup';
import FormField from '../components/shared/FormField';
import api from '../api/api';
import logger from '../logger/logger';
import { devices } from '../data/constants';
import { usePayments } from '../hooks/usePayments';
import { ModalBackgroundStyled } from '../styles/Main';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;

  @media only screen and ${devices.md} {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0;
`;

// Action Button
const RunAnalysisButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  margin: 24px auto;
  padding: 16px 32px;
  background: ${({ theme, disabled }) => disabled ? theme.colors.themeActiveDisabled : theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.colors.ff};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme, disabled }) => disabled ? theme.colors.themeActiveDisabled : theme.colors.activeColorDarkened};
  }
`;

// Query Limit Modal Styles
const QueryLimitModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const QueryLimitTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0 0 12px 0;
`;

const QueryLimitMessage = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const QueryLimitButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const QueryLimitButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.colors.ff};
  
  ${({ primary, theme }) => primary ? `
    background: ${theme.colors.activeColor};
    color: #fff;
    border: none;
    
    &:hover {
      background: ${theme.colors.activeColorDarkened};
    }
  ` : `
    background: transparent;
    color: ${theme.colors.cardText};
    border: 1px solid ${theme.colors.borderColor};
    
    &:hover {
      background: ${theme.colors.opacityColor};
    }
  `}
`;

// Validation Error Display
const ErrorList = styled.div`
  background: ${({ theme }) => theme.colors.errorColor}10;
  border: 1px solid ${({ theme }) => theme.colors.errorColor};
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px auto;
  max-width: 400px;
`;

const ErrorItem = styled.p`
  color: ${({ theme }) => theme.colors.errorColor};
  font-size: 14px;
  margin: 4px 0;
  
  &:before {
    content: "• ";
  }
`;

// CapEx Category Labels
const CapExCategory = styled.div`
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: ${({ first }) => first ? '0' : '16px'};
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.opacityColor || '#e5e5e5'};
`;

const CapExRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

// CapEx Explanation Box
const CapExExplanation = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.activeColor}10;
  border: 1px solid ${({ theme }) => theme.colors.activeColor}30;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.cardText};

  strong {
    color: ${({ theme }) => theme.colors.activeColor};
  }

  p {
    margin: 0 0 10px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }
`;

// CapEx Tier Toggle
const CapExTierContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const CapExTierLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cardText};
`;

const CapExTierToggle = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  padding: 4px;
`;

const CapExTierButton = styled.button`
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.cardBG};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.cardText};

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const CapExTierDescription = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.subTextColor};
  font-style: italic;
`;

// Remember Values Toggle
const RememberToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const RememberToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cardText};
  cursor: pointer;
  user-select: none;
`;

const RememberToggleCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.activeColor};
`;

const RememberToggleHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin-left: auto;
`;

// Results Styles
const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;


  @media only screen and ${devices.md} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryCard = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  text-align: center;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const SummaryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0 auto 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme, $positive }) => $positive ? theme.colors.successColor : theme.colors.cardText};
  
  @media only screen and ${devices.md} {
    font-size: 20px;
  }
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin-top: 4px;
`;

// Results Tab Explanation Box
const TabExplanation = styled.div`
  background: ${({ theme }) => theme.colors.opacityColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.cardText};

  strong {
    color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const TabsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  padding: 4px;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.subTextColor};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  }
`;

const ChartContainer = styled.div`
  height: 300px;
  margin: 16px 0;
`;

const DetailsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.subTextColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  }
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.cardText};
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.opacityColor};
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.successColor};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const ExportDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.cardBG};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 12px;
  min-width: 200px;
  z-index: 100;
`;

const ExportCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cardText};
  cursor: pointer;
  margin-bottom: 12px;
  
  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ExportConfirmButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  
  &:hover {
    opacity: 0.9;
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.thead`
  background: ${({ theme }) => theme.colors.activeColor};
  
  th {
    padding: 12px 10px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    color: #ffffff;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    white-space: nowrap;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    background: ${({ theme }) => theme.colors.cardBG};
    transition: background 0.2s ease;
    
    &:nth-child(even) {
      background: ${({ theme }) => theme.colors.opacityColor};
    }
    
    &:hover {
      background: ${({ theme }) => theme.colors.lightActiveHover};
    }
  }
  
  td {
    padding: 12px 10px;
    color: ${({ theme }) => theme.colors.cardText};
    white-space: nowrap;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
`;

const ManualModePage = ({ user, onBack }) => {
  // Theme for dynamic colors
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Payments hook for query limit checking
  const { updateQueryLimit, checkQueryLimit, getCustomer, getCurrentPlan, getProducts, manageSubscription } = usePayments();
  
  // Query limit modal state
  const [showQueryLimitModal, setShowQueryLimitModal] = useState(false);
  const [noSubscription, setNoSubscription] = useState(false);
  const [freeResetDate, setFreeResetDate] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Fetch subscription info on mount
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (user?.uid) {
        const [sub, cust, prods] = await Promise.all([
          getCurrentPlan(user.uid),
          getCustomer(user.uid),
          getProducts()
        ]);
        setSubscription(sub);
        setCustomer(cust);
        if (prods) {
          setPlans(prods);
        }
      }
    };
    fetchSubscriptionInfo();
  }, [user?.uid, getCurrentPlan, getCustomer, getProducts]);
  
  // Format reset date
  const getResetInfo = () => {
    if (!subscription?.current_period_end_date) return null;
    
    // Find the plan by priceId (more reliable than planId)
    const currentPriceId = subscription?.plan?.price?.id || customer?.priceId;
    const currentPlan = plans.find(p => p.prices?.priceId === currentPriceId);
    
    const planName = currentPlan?.name || 'your plan';
    const queries = currentPlan?.metadata?.queries || currentPlan?.up_to || '0';
    const queryDisplay = queries === '-1' || queries === -1 ? 'unlimited' : queries;
    
    return {
      date: subscription.current_period_end_date,
      queries: queryDisplay,
      planName
    };
  };
  
  // CapEx tier for cost estimates
  const [capexTier, setCapexTier] = useState('standard');
  
  // CapEx tier multipliers (real-world contractor pricing)
  const capexTierMultipliers = {
    budget: 1.0,    // DIY/handyman
    standard: 1.5,  // Licensed contractor
    premium: 2.25,  // High-end/specialized
  };

  // CapEx suggestion formulas based on square footage and tier
  const getCapExSuggestion = (item, sqft) => {
    if (!sqft || sqft <= 0) return '';
    const multiplier = capexTierMultipliers[capexTier];
    const baseSuggestions = {
      // Items that scale with sq ft (base rates for Budget tier)
      roofCost: Math.round(sqft * 3.00 * multiplier),           // $3.00/sq ft
      hvacCost: Math.round(sqft * 2.00 * multiplier),           // $2.00/sq ft
      plumbingCost: Math.round(sqft * 1.50 * multiplier),       // $1.50/sq ft
      flooringCost: Math.round(sqft * 1.50 * multiplier),       // $1.50/sq ft
      windowsCost: Math.round(sqft * 2.50 * multiplier),        // $2.50/sq ft
      paintCost: Math.round(sqft * 1.00 * multiplier),          // $1.00/sq ft
      structureCost: Math.round(sqft * 5.00 * multiplier),      // $5.00/sq ft
      // Fixed items
      waterHeaterCost: Math.round(600 * multiplier),
      appliancesCost: Math.round(1000 * multiplier),
      drivewayparkingLotCost: Math.round(3000 * multiplier),
      cabinetscountersCost: Math.round(3000 * multiplier),
      componentsCost: Math.round(1000 * multiplier),
      landscapingCost: Math.round(1000 * multiplier),
      otherCapexCost: 0,
    };
    return baseSuggestions[item] ? `Est. $${baseSuggestions[item].toLocaleString()}` : '';
  };
  
  // State for accordion sections
  const [expandedSection, setExpandedSection] = useState(1);
  
  // Default values for form fields
  const defaultProperty = {
    purchasePrice: 185000,
    sqft: 1500,
    unit1Rent: 1450,
    unit2Rent: 0,
    unit3Rent: 0,
    unit4Rent: 0,
    annualTaxes: 2100,
    hoa: 0,
  };
  
  const defaultParams = {
    // Loan Terms
    loanAmount: 75,
    interestRate: 7.0,
    loanTerm: 30,
    amortizationPeriod: 30,
    interestOnlyPeriod: 0,
    // Operating Expenses
    vacancy: 5,
    managementFee: 8,
    insurance: 1200,
    mortgageInsurance: 0,
    utilities: 0,
    advertising: 0,
    taxService: 0,
    otherExpenses: 0,
    // Market Assumptions
    rentIncrease: 2,
    expenseIncrease: 2,
    propertyTaxIncrease: 2,
    // CapEx (ages)
    roofAge: 25,
    hvacAge: 20,
    plumbingAge: 30,
    waterHeaterAge: 10,
    appliancesAge: 10,
    flooringAge: 6,
    drivewayparkingLotAge: 50,
    windowsAge: 50,
    paintAge: 5,
    cabinetscountersAge: 20,
    structureAge: 50,
    componentsAge: 10,
    landscapingAge: 10,
    otherCapexAge: 10,
    // CapEx (costs)
    roofCost: 0,
    hvacCost: 0,
    plumbingCost: 0,
    waterHeaterCost: 0,
    appliancesCost: 0,
    flooringCost: 0,
    drivewayparkingLotCost: 0,
    windowsCost: 0,
    paintCost: 0,
    cabinetscountersCost: 0,
    structureCost: 0,
    componentsCost: 0,
    landscapingCost: 0,
    otherCapexCost: 0,
  };
  
  const defaultValuation = {
    exitCapRate: 7,
    holdPeriod: 5,
  };
  
  // Remember values toggle state (persisted to localStorage)
  const [rememberValues, setRememberValues] = useState(() => {
    try {
      return localStorage.getItem('propninja_remember_manual') === 'true';
    } catch {
      return false;
    }
  });
  
  // Load saved values from localStorage if remember is enabled
  const loadSavedValues = useCallback((key, defaults) => {
    if (!rememberValues) return defaults;
    try {
      const saved = localStorage.getItem(`propninja_manual_${key}`);
      if (saved) {
        return { ...defaults, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Failed to load saved form values:', e);
    }
    return defaults;
  }, [rememberValues]);
  
  // Property details
  const [property, setProperty] = useState(() => loadSavedValues('property', defaultProperty));
  
  // Loan & expense parameters
  const [params, setParams] = useState(() => loadSavedValues('params', defaultParams));
  
  // Valuation assumptions
  const [valuation, setValuation] = useState(() => loadSavedValues('valuation', defaultValuation));
  
  // Save to localStorage when values change (if remember is enabled)
  useEffect(() => {
    if (rememberValues) {
      try {
        localStorage.setItem('propninja_manual_property', JSON.stringify(property));
        localStorage.setItem('propninja_manual_params', JSON.stringify(params));
        localStorage.setItem('propninja_manual_valuation', JSON.stringify(valuation));
      } catch (e) {
        console.warn('Failed to save form values:', e);
      }
    }
  }, [property, params, valuation, rememberValues]);
  
  // Handle remember toggle change
  const handleRememberToggle = (enabled) => {
    setRememberValues(enabled);
    try {
      localStorage.setItem('propninja_remember_manual', String(enabled));
      if (!enabled) {
        // Clear saved values when disabling
        localStorage.removeItem('propninja_manual_property');
        localStorage.removeItem('propninja_manual_params');
        localStorage.removeItem('propninja_manual_valuation');
      }
    } catch (e) {
      console.warn('Failed to save remember preference:', e);
    }
  };
  
  // Select all on focus for easy replacement (works in both modes)
  const handleFieldFocus = useCallback((e) => {
    e.target.select();
  }, []);
  
  // Results state
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('cashflow');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'chart'
  const [showDetails, setShowDetails] = useState(false); // Show extra columns in cash flow table
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportIncludeInputs, setExportIncludeInputs] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // Validate a single field and return error message or null
  const validateField = (field, value) => {
    switch (field) {
      case 'purchasePrice':
        return (!value || value <= 0) ? 'Required' : null;
      case 'interestRate':
        return (!value || value <= 0) ? 'Required' : null;
      case 'amortizationPeriod':
        return (!value || value <= 0) ? 'Required' : null;
      case 'exitCapRate':
        return (!value || value <= 0) ? 'Required' : null;
      case 'loanAmount':
        return (value < 0 || value > 100) ? '0-100%' : null;
      case 'vacancy':
        return (value < 0 || value > 100) ? '0-100%' : null;
      case 'managementFee':
        return (value < 0 || value > 100) ? '0-100%' : null;
      default:
        return null;
    }
  };

  // Validate required fields before submission
  const validateRequiredFields = () => {
    const errors = [];
    
    // Critical - will crash or give nonsense results
    if (!property.purchasePrice || property.purchasePrice <= 0) {
      errors.push('Purchase Price is required and must be greater than 0');
    }
    if (!params.interestRate || params.interestRate <= 0) {
      errors.push('Interest Rate is required and must be greater than 0');
    }
    if (!params.amortizationPeriod || params.amortizationPeriod <= 0) {
      errors.push('Amortization Period is required and must be greater than 0');
    }
    if (!valuation.exitCapRate || valuation.exitCapRate <= 0) {
      errors.push('Exit Cap Rate is required and must be greater than 0');
    }
    
    // At least one unit rent required
    const totalRent = property.unit1Rent + property.unit2Rent + 
                      property.unit3Rent + property.unit4Rent;
    if (totalRent <= 0) {
      errors.push('At least one Unit Rent must be greater than 0');
    }
    
    // LTV can be 0 (cash purchase) but must be valid range
    if (params.loanAmount < 0 || params.loanAmount > 100) {
      errors.push('Loan Amount (LTV) must be between 0 and 100');
    }
    
    return errors;
  };

  const handlePropertyChange = (field, value) => {
    // Allow empty string for user input, convert to number for storage
    const numValue = value === '' ? '' : (parseFloat(value) || 0);
    setProperty(prev => ({ ...prev, [field]: numValue }));
    setValidationErrors([]); // Clear submit errors
    
    // Real-time field validation - treat empty as 0 for validation
    const error = validateField(field, numValue === '' ? 0 : numValue);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleParamChange = (field, value) => {
    const numValue = value === '' ? '' : (parseFloat(value) || 0);
    setParams(prev => ({ ...prev, [field]: numValue }));
    setValidationErrors([]); // Clear submit errors
    
    // Real-time field validation
    const error = validateField(field, numValue === '' ? 0 : numValue);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleValuationChange = (field, value) => {
    const numValue = value === '' ? '' : (parseFloat(value) || 0);
    setValuation(prev => ({ ...prev, [field]: numValue }));
    setValidationErrors([]); // Clear submit errors
    
    // Real-time field validation
    const error = validateField(field, numValue === '' ? 0 : numValue);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  // Build summary text for collapsed sections
  const getPropertySummary = () => {
    const totalRent = property.unit1Rent + property.unit2Rent + property.unit3Rent + property.unit4Rent;
    return `$${property.purchasePrice.toLocaleString()}, $${totalRent.toLocaleString()}/mo rent`;
  };

  const getParametersSummary = () => {
    const totalCapEx = params.roofCost + params.hvacCost + params.plumbingCost + 
                       params.waterHeaterCost + params.appliancesCost + params.flooringCost;
    return `${params.loanAmount}% LTV, ${params.interestRate}% rate, $${(totalCapEx/1000).toFixed(1)}K CapEx`;
  };

  const getValuationSummary = () => {
    return `${valuation.exitCapRate}% exit cap, ${valuation.holdPeriod}yr hold`;
  };

  // Calculate and run analysis
  const runAnalysis = async () => {
    // Validate required fields first
    const errors = validateRequiredFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Check query limit before running analysis
    // Show loading immediately for responsive UI
    setIsLoading(true);
    
    const queryCheck = await checkQueryLimit(user.uid);
    if (!queryCheck.allowed) {
      setNoSubscription(queryCheck.noSubscription || false);
      setFreeResetDate(queryCheck.freeResetDate || null);
      setShowQueryLimitModal(true);
      setIsLoading(false);
      return;
    }
    
    // Decrement query limit (do this before the request)
    const updateResult = await updateQueryLimit(user.uid);
    if (!updateResult.success) {
      setShowQueryLimitModal(true);
      setIsLoading(false);
      return;
    }
    
    // Build flat property data for the calculator (no { value: X } wrappers)
    const propertyData = {
      sqft: property.sqft,
      purchase_price: property.purchasePrice,
      unit_1_rent: property.unit1Rent,
      unit_2_rent: property.unit2Rent,
      unit_3_rent: property.unit3Rent,
      unit_4_rent: property.unit4Rent,
      property_tax_rate_pct: (property.annualTaxes / property.purchasePrice) * 100,
      homeowners_insurance_cost: params.insurance,
      mortgage_insurance: params.mortgageInsurance,
      management_fee: params.managementFee,
      monthly_utilities_cost: params.utilities,
      other_expenses: params.otherExpenses,
      interest_rate_pct: params.interestRate,
      loan_term: params.loanTerm,
      amortization_period: params.amortizationPeriod,
      rent_increase_pct: params.rentIncrease,
      expenses_increase_pct: params.expenseIncrease,
      annual_property_tax_increase_pct: params.propertyTaxIncrease,
      advertising: params.advertising,
      tax_service: params.taxService,
      general_vacancy: params.vacancy,
      loan_amount: params.loanAmount,
      interest_only_period: params.interestOnlyPeriod,
      cap_rate: valuation.exitCapRate,
      coc_pct: 0,
      coc_term: valuation.holdPeriod,
      // CapEx ages - all 14 items
      roof_age: params.roofAge,
      hvac_age: params.hvacAge,
      plumbing_age: params.plumbingAge,
      water_heater_age: params.waterHeaterAge,
      appliances_age: params.appliancesAge,
      flooring_age: params.flooringAge,
      'driveway/parking_lot_age': params.drivewayparkingLotAge,
      windows_age: params.windowsAge,
      paint_age: params.paintAge,
      'cabinets/counters_age': params.cabinetscountersAge,
      structure_age: params.structureAge,
      components_age: params.componentsAge,
      landscaping_age: params.landscapingAge,
      other_age: params.otherCapexAge,
      // CapEx costs - all 14 items
      roof_replacement_cost: params.roofCost,
      hvac_replacement_cost: params.hvacCost,
      plumbing_replacement_cost: params.plumbingCost,
      water_heater_replacement_cost: params.waterHeaterCost,
      appliances_replacement_cost: params.appliancesCost,
      flooring_replacement_cost: params.flooringCost,
      'driveway/parking_lot_replacement_cost': params.drivewayparkingLotCost,
      windows_replacement_cost: params.windowsCost,
      paint_replacement_cost: params.paintCost,
      'cabinets/counters_replacement_cost': params.cabinetscountersCost,
      structure_replacement_cost: params.structureCost,
      components_replacement_cost: params.componentsCost,
      landscaping_replacement_cost: params.landscapingCost,
      other_replacement_cost: params.otherCapexCost,
    };
    
    try {
      const response = await api.post('/calculate', {
        mode: 'manual',
        form_data: { manual: propertyData },  // Correct key: form_data with flat values
        zip_codes: ['manual'],
        capex_lifespans: {
          roof: 25,
          hvac: 20,
          plumbing: 30,
          water_heater: 10,
          appliances: 10,
          flooring: 6,
          'driveway/parking_lot': 50,
          windows: 50,
          paint: 5,
          'cabinets/counters': 20,
          structure: 50,
          components: 10,
          landscaping: 10,
          other: 0
        }
      });
      
      // Map backend response keys to frontend expected keys
      setResults({
        valuation: response.data.valuation,
        cashFlow: response.data.cash_flow,                    // backend: cash_flow
        amortization: response.data.amortization_schedule,    // backend: amortization_schedule
        annual_irrs: response.data.annual_irrs,
        coc: response.data.coc
      });
      setExpandedSection('results');
      logger.info('Analysis complete');
    } catch (error) {
      logger.error('Error running analysis: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${parseFloat(value).toFixed(2)}%`;
  };

  // Keyboard shortcut: Ctrl+Enter to run analysis
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        // Trigger click on the run button if conditions are met
        const runButton = document.querySelector('[data-run-analysis]');
        if (runButton && !runButton.disabled) {
          runButton.click();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Render results tables based on active tab
  const renderResultsTable = () => {
    if (!results) return null;

    // Chart rendering for supported tabs
    if (viewMode === 'chart') {
      switch (activeTab) {
        case 'cashflow':
          if (!results.cashFlow || results.cashFlow.length === 0) return <p>No cash flow data available.</p>;
          return (
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results.cashFlow} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                  <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Cash Flow']}
                    contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                    labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                    itemStyle={{ color: '#333' }}
                  />
                  <ReferenceLine y={0} stroke={theme.colors.textSecondary} />
                  <Bar dataKey="Cash Flow" fill={theme.colors.successColor} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          );
        
        case 'irr':
          if (!results.annual_irrs || results.annual_irrs.length === 0) return <p>No IRR data available.</p>;
          return (
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={results.annual_irrs} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                  <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'IRR']}
                    contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                    labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                    itemStyle={{ color: '#333' }}
                  />
                  <ReferenceLine y={0} stroke={theme.colors.errorColor} strokeDasharray="3 3" />
                  <ReferenceLine y={10} stroke={theme.colors.successColor} strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="IRR" stroke={theme.colors.accent} strokeWidth={2} dot={{ fill: theme.colors.accent }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          );
        
        case 'coc':
          if (!results.coc || results.coc.length === 0) return <p>No COC data available.</p>;
          return (
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={results.coc} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                  <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    formatter={(value) => [value !== null ? `${value}%` : 'N/A', 'COC']}
                    contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                    labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                    itemStyle={{ color: '#333' }}
                  />
                  <ReferenceLine y={0} stroke={theme.colors.errorColor} strokeDasharray="3 3" />
                  <ReferenceLine y={8} stroke={theme.colors.successColor} strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="COC" stroke={theme.colors.warningColor} strokeWidth={2} dot={{ fill: theme.colors.warningColor }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          );
        
        case 'amortization':
          if (!results.amortization || results.amortization.length === 0) return <p>No amortization data available.</p>;
          return (
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results.amortization} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                  <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                    contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                    labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                    itemStyle={{ color: '#333' }}
                  />
                  <Bar dataKey="Principal Payment" stackId="payment" fill={theme.colors.successColor} radius={[0, 0, 0, 0]} name="Principal" />
                  <Bar dataKey="Interest Payment" stackId="payment" fill={theme.colors.errorColor} radius={[4, 4, 0, 0]} name="Interest" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          );
        
        case 'valuation':
          // Valuation is a single row, table view makes more sense
          break;
        
        default:
          return null;
      }
    }

    // Table rendering (default)
    switch (activeTab) {
      case 'cashflow':
        return results.cashFlow && results.cashFlow.length > 0 ? (
          <TableWrapper>
            <DataTable>
              <TableHeader>
                <tr>
                  <th>Year</th>
                  <th>Gross Rev</th>
                  <th>Vacancy</th>
                  <th>EGR</th>
                  {showDetails && <th>Prop Tax</th>}
                  {showDetails && <th>CapEx</th>}
                  <th>Expenses</th>
                  <th>NOI</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Cash Flow</th>
                </tr>
              </TableHeader>
              <TableBody>
                {results.cashFlow.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td>{formatCurrency(row["Potential Gross Revenue"])}</td>
                    <td style={{ color: row["Vacancy Loss"] > 0 ? '#dc3545' : 'inherit' }}>{row["Vacancy Loss"] > 0 ? '-' : ''}{formatCurrency(row["Vacancy Loss"])}</td>
                    <td>{formatCurrency(row["Effective Gross Revenue"])}</td>
                    {showDetails && <td style={{ color: row["Property Taxes"] > 0 ? '#dc3545' : 'inherit' }}>{row["Property Taxes"] > 0 ? '-' : ''}{formatCurrency(row["Property Taxes"])}</td>}
                    {showDetails && <td style={{ color: row["CapEx"] > 0 ? '#dc3545' : 'inherit' }}>{row["CapEx"] > 0 ? '-' : ''}{formatCurrency(row["CapEx"])}</td>}
                    <td style={{ color: row["Operating Expenses"] > 0 ? '#dc3545' : 'inherit' }}>{row["Operating Expenses"] > 0 ? '-' : ''}{formatCurrency(row["Operating Expenses"])}</td>
                    <td>{formatCurrency(row["NOI"])}</td>
                    <td style={{ color: row["Principal Payment"] > 0 ? '#dc3545' : 'inherit' }}>{row["Principal Payment"] > 0 ? '-' : ''}{formatCurrency(row["Principal Payment"])}</td>
                    <td style={{ color: row["Interest Payment"] > 0 ? '#dc3545' : 'inherit' }}>{row["Interest Payment"] > 0 ? '-' : ''}{formatCurrency(row["Interest Payment"])}</td>
                    <td style={{ color: row["Cash Flow"] >= 0 ? '#28a745' : '#dc3545' }}>{formatCurrency(row["Cash Flow"])}</td>
                  </tr>
                ))}
              </TableBody>
            </DataTable>
          </TableWrapper>
        ) : <p>No cash flow data available.</p>;

      case 'amortization':
        return results.amortization && results.amortization.length > 0 ? (
          <TableWrapper>
            <DataTable>
              <TableHeader>
                <tr>
                  <th>Year</th>
                  <th>Payment</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Balance</th>
                </tr>
              </TableHeader>
              <TableBody>
                {results.amortization.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td>{formatCurrency(row["Annual Payment"])}</td>
                    <td>{formatCurrency(row["Principal Payment"])}</td>
                    <td>{formatCurrency(row["Interest Payment"])}</td>
                    <td>{formatCurrency(row["Remaining Balance"])}</td>
                  </tr>
                ))}
              </TableBody>
            </DataTable>
          </TableWrapper>
        ) : <p>No amortization data available.</p>;

      case 'irr':
        return results.annual_irrs && results.annual_irrs.length > 0 ? (
          <TableWrapper>
            <DataTable>
              <TableHeader>
                <tr>
                  <th>Year</th>
                  <th>IRR</th>
                </tr>
              </TableHeader>
              <TableBody>
                {results.annual_irrs.map((row, index) => {
                  // If TotalReturnDollar exists, it's a fallback calculation (total return, not true IRR)
                  const isFallback = row.TotalReturnDollar !== null && row.TotalReturnDollar !== undefined;
                  const displayValue = isFallback 
                    ? `${row.IRR}%/${row.TotalReturnDollar < 0 ? '-' : ''}$${Math.abs(row.TotalReturnDollar).toLocaleString()}`
                    : `${row.IRR}%`;
                  return (
                    <tr key={index}>
                      <td>{row.Year}</td>
                      <td style={{ color: row.IRR < 0 ? theme.colors.errorColor : row.IRR >= 10 ? theme.colors.successColor : 'inherit' }}>
                        {displayValue}
                      </td>
                    </tr>
                  );
                })}
              </TableBody>
            </DataTable>
          </TableWrapper>
        ) : <p>No IRR data available.</p>;

      case 'coc':
        return results.coc && results.coc.length > 0 ? (
          <TableWrapper>
            <DataTable>
              <TableHeader>
                <tr>
                  <th>Year</th>
                  <th>Cash-on-Cash Return</th>
                </tr>
              </TableHeader>
              <TableBody>
                {results.coc.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td style={{ color: row.COC !== null && row.COC < 0 ? theme.colors.errorColor : row.COC !== null && row.COC >= 8 ? theme.colors.successColor : 'inherit' }}>
                      {row.COC !== null ? `${row.COC}%` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </TableBody>
            </DataTable>
          </TableWrapper>
        ) : <p>No COC data available.</p>;

      case 'valuation':
        return results.valuation && Object.keys(results.valuation).length > 0 ? (
          <TableWrapper>
            <DataTable>
              <TableHeader>
                <tr>
                  <th>Sale Price</th>
                  <th>Net Proceeds</th>
                  <th>Cap Rate</th>
                  <th>Final NOI</th>
                </tr>
              </TableHeader>
              <TableBody>
                <tr>
                  <td>{formatCurrency(results.valuation["Sale Price"])}</td>
                  <td>{formatCurrency(results.valuation["Net Sales Proceeds"])}</td>
                  <td>{formatPercent(results.valuation["Cap Rate"] * 100)}</td>
                  <td>{formatCurrency(results.valuation["Final NOI"])}</td>
                </tr>
              </TableBody>
            </DataTable>
          </TableWrapper>
        ) : <p>No valuation data available.</p>;

      default:
        return null;
    }
  };

  // Handle export to Excel
  const handleExport = () => {
    const formData = {
      property: {
        purchasePrice: property.purchasePrice,
        sqft: property.sqft,
        monthlyRent: property.monthlyRent
      },
      params: {
        loanAmount: params.loanAmount,
        interestRate: params.interestRate,
        loanTerm: params.loanTerm,
        amortizationPeriod: params.amortizationPeriod,
        vacancy: params.vacancy,
        managementFee: params.managementFee,
        rentIncrease: params.rentIncrease,
        expenseIncrease: params.expenseIncrease
      },
      valuation: {
        holdPeriod: valuation.holdPeriod,
        capRate: valuation.exitCapRate
      }
    };
    
    exportManualModeResults(results, formData, exportIncludeInputs);
    setShowExportDropdown(false);
    setExportIncludeInputs(false);
  };

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={onBack}>← Back to Mode Selection</BackButton>
        <PageTitle>Single Property Analysis</PageTitle>
      </PageHeader>

      {/* Remember Values Toggle */}
      <RememberToggleContainer>
        <RememberToggleLabel>
          <RememberToggleCheckbox
            type="checkbox"
            checked={rememberValues}
            onChange={(e) => handleRememberToggle(e.target.checked)}
          />
          <IoSaveOutline size={16} />
          Remember my values
        </RememberToggleLabel>
        <RememberToggleHint>
          {rememberValues ? 'Values will persist on refresh' : 'Remember your property inputs throughout this session'}
        </RememberToggleHint>
      </RememberToggleContainer>

      {/* Section 1: Property Details */}
      <AccordionSection
        stepNumber={1}
        title="Property Details"
        summary={expandedSection !== 1 ? getPropertySummary() : null}
        isExpanded={expandedSection === 1}
        isCompleted={property.purchasePrice > 0 && property.unit1Rent > 0}
        onToggle={() => setExpandedSection(expandedSection === 1 ? null : 1)}
      >
        <InputGroup title="Purchase & Income">
          <FormField
            label="Purchase Price"
            prefix="$"
            value={property.purchasePrice}
            onChange={(e) => handlePropertyChange('purchasePrice', e.target.value)}
            onFocus={handleFieldFocus}
            error={fieldErrors.purchasePrice}
          />
          <FormField
            label="Square Footage"
            suffix="sqft"
            value={property.sqft}
            onChange={(e) => handlePropertyChange('sqft', e.target.value)}
            onFocus={handleFieldFocus}
          />
          <FormField
            label="Unit 1 Rent"
            prefix="$"
            suffix="/mo"
            value={property.unit1Rent}
            onChange={(e) => handlePropertyChange('unit1Rent', e.target.value)}
            onFocus={handleFieldFocus}
          />
          <FormField
            label="Unit 2 Rent"
            prefix="$"
            suffix="/mo"
            value={property.unit2Rent}
            onChange={(e) => handlePropertyChange('unit2Rent', e.target.value)}
            onFocus={handleFieldFocus}
            description="Leave 0 if single unit"
          />
          <FormField
            label="Unit 3 Rent"
            prefix="$"
            suffix="/mo"
            value={property.unit3Rent}
            onChange={(e) => handlePropertyChange('unit3Rent', e.target.value)}
            onFocus={handleFieldFocus}
            description="Leave 0 if 2 or fewer units"
          />
          <FormField
            label="Unit 4 Rent"
            prefix="$"
            suffix="/mo"
            value={property.unit4Rent}
            onChange={(e) => handlePropertyChange('unit4Rent', e.target.value)}
            onFocus={handleFieldFocus}
            description="Leave 0 if 3 or fewer units"
          />
        </InputGroup>

        <InputGroup title="Taxes & Fees">
          <FormField
            label="Annual Taxes"
            prefix="$"
            suffix="/yr"
            value={property.annualTaxes}
            onChange={(e) => handlePropertyChange('annualTaxes', e.target.value)}
            onFocus={handleFieldFocus}
          />
          <FormField
            label="HOA"
            prefix="$"
            suffix="/mo"
            value={property.hoa}
            onChange={(e) => handlePropertyChange('hoa', e.target.value)}
            onFocus={handleFieldFocus}
          />
        </InputGroup>
      </AccordionSection>

      {/* Section 2: Loan & Expenses */}
      <AccordionSection
        stepNumber={2}
        title="Loan & Expenses"
        summary={expandedSection !== 2 ? getParametersSummary() : null}
        isExpanded={expandedSection === 2}
        isCompleted={true}
        onToggle={() => setExpandedSection(expandedSection === 2 ? null : 2)}
      >
        <InputGroup title="Loan Terms">
          <FormField
            label="Loan Amount"
            suffix="%"
            value={params.loanAmount}
            onChange={(e) => handleParamChange('loanAmount', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Percentage of purchase price financed through a mortgage. Example: $100,000 purchase with $35,000 down = 65% loan amount."
            error={fieldErrors.loanAmount}
          />
          <FormField
            label="Interest Rate"
            suffix="%"
            value={params.interestRate}
            onChange={(e) => handleParamChange('interestRate', e.target.value)}
            onFocus={handleFieldFocus}
            step={0.125}
            error={fieldErrors.interestRate}
            tooltip="Annual interest rate on your mortgage loan."
          />
          <FormField
            label="Loan Term"
            suffix="yrs"
            value={params.loanTerm}
            onChange={(e) => handleParamChange('loanTerm', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Length of time until loan matures. If shorter than amortization, a balloon payment is due at the end."
          />
          <FormField
            label="Amortization"
            suffix="yrs"
            value={params.amortizationPeriod}
            onChange={(e) => handleParamChange('amortizationPeriod', e.target.value)}
            onFocus={handleFieldFocus}
            error={fieldErrors.amortizationPeriod}
            tooltip="Length of time over which the loan payments are calculated. Can differ from loan term—for example, a 5-year loan with 30-year amortization results in a balloon payment at the end."
          />
        </InputGroup>

        <InputGroup title="Operating Expenses">
          <FormField
            label="Vacancy"
            suffix="%"
            value={params.vacancy}
            onChange={(e) => handleParamChange('vacancy', e.target.value)}
            onFocus={handleFieldFocus}
            error={fieldErrors.vacancy}
            tooltip="Annual percentage of time units are vacant. Accounts for tenant turnover, lease gaps, and renovation time between tenants. Typically 5-10% for residential."
          />
          <FormField
            label="Management"
            suffix="%"
            value={params.managementFee}
            onChange={(e) => handleParamChange('managementFee', e.target.value)}
            onFocus={handleFieldFocus}
            error={fieldErrors.managementFee}
            tooltip="Property management fee as a percentage of gross rent. Typically 8-12% for professional management."
          />
          <FormField
            label="Insurance"
            prefix="$"
            suffix="/yr"
            value={params.insurance}
            onChange={(e) => handleParamChange('insurance', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Annual homeowner's or landlord insurance premium."
          />
          <FormField
            label="Utilities"
            prefix="$"
            suffix="/mo"
            value={params.utilities}
            onChange={(e) => handleParamChange('utilities', e.target.value)}
            onFocus={handleFieldFocus}
          />
        </InputGroup>

        <InputGroup title="CapEx Reserves">
          <CapExExplanation>
            <p>
              <strong>Capital Expenditures (CapEx)</strong> are major repairs and replacements that occur during property ownership—roof, HVAC, plumbing, etc. Unlike regular maintenance, these are large, infrequent expenses that can significantly impact your cash flow if you're not prepared.
            </p>
            <p>
              <strong>How it works:</strong> For each component, enter the estimated replacement cost and its current age. We calculate your annual reserve by dividing the cost by the remaining useful life:
            </p>
            <ul>
              <li><strong>Annual Reserve</strong> = Replacement Cost ÷ (Lifespan − Current Age)</li>
              <li>The "/ X yrs" shown next to each age field is that component's expected lifespan</li>
              <li>Default ages assume worst-case (fully aged out) for conservative estimates</li>
            </ul>
            <p>
              <strong>Estimated costs</strong> (shown as grey placeholders) are calculated from the square footage you entered in Step 1. Use the toggle below to adjust estimates based on who does the work—Budget for DIY/handyman, Standard for licensed contractors, or Premium for high-end specialists.
            </p>
          </CapExExplanation>
          <CapExTierContainer>
            <CapExTierLabel>Estimate Level:</CapExTierLabel>
            <CapExTierToggle>
              <CapExTierButton 
                $active={capexTier === 'budget'} 
                onClick={() => setCapexTier('budget')}
              >
                Budget
              </CapExTierButton>
              <CapExTierButton 
                $active={capexTier === 'standard'} 
                onClick={() => setCapexTier('standard')}
              >
                Standard
              </CapExTierButton>
              <CapExTierButton 
                $active={capexTier === 'premium'} 
                onClick={() => setCapexTier('premium')}
              >
                Premium
              </CapExTierButton>
            </CapExTierToggle>
            <CapExTierDescription>
              {capexTier === 'budget' && 'DIY or budget contractors'}
              {capexTier === 'standard' && 'Licensed general contractors'}
              {capexTier === 'premium' && 'High-end or specialized contractors'}
            </CapExTierDescription>
          </CapExTierContainer>
          <CapExCategory first>Major Systems</CapExCategory>
          <CapExRow>
            <FormField
              label="Roof"
              prefix="$"
              value={params.roofCost || ''}
              onChange={(e) => handleParamChange('roofCost', e.target.value)}
              placeholder={getCapExSuggestion('roofCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 25 yrs"
              value={params.roofAge}
              onChange={(e) => handleParamChange('roofAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="HVAC"
              prefix="$"
              value={params.hvacCost || ''}
              onChange={(e) => handleParamChange('hvacCost', e.target.value)}
              placeholder={getCapExSuggestion('hvacCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 20 yrs"
              value={params.hvacAge}
              onChange={(e) => handleParamChange('hvacAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Plumbing"
              prefix="$"
              value={params.plumbingCost || ''}
              onChange={(e) => handleParamChange('plumbingCost', e.target.value)}
              placeholder={getCapExSuggestion('plumbingCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 30 yrs"
              value={params.plumbingAge}
              onChange={(e) => handleParamChange('plumbingAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Water Heater"
              prefix="$"
              value={params.waterHeaterCost || ''}
              onChange={(e) => handleParamChange('waterHeaterCost', e.target.value)}
              placeholder={getCapExSuggestion('waterHeaterCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 10 yrs"
              value={params.waterHeaterAge}
              onChange={(e) => handleParamChange('waterHeaterAge', e.target.value)}
              width="80px"
            />
          </CapExRow>

          <CapExCategory>Interior</CapExCategory>
          <CapExRow>
            <FormField
              label="Appliances"
              prefix="$"
              value={params.appliancesCost || ''}
              onChange={(e) => handleParamChange('appliancesCost', e.target.value)}
              placeholder={getCapExSuggestion('appliancesCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 10 yrs"
              value={params.appliancesAge}
              onChange={(e) => handleParamChange('appliancesAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Flooring"
              prefix="$"
              value={params.flooringCost || ''}
              onChange={(e) => handleParamChange('flooringCost', e.target.value)}
              placeholder={getCapExSuggestion('flooringCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 6 yrs"
              value={params.flooringAge}
              onChange={(e) => handleParamChange('flooringAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Paint"
              prefix="$"
              value={params.paintCost || ''}
              onChange={(e) => handleParamChange('paintCost', e.target.value)}
              placeholder={getCapExSuggestion('paintCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 5 yrs"
              value={params.paintAge}
              onChange={(e) => handleParamChange('paintAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Cabinets/Counters"
              prefix="$"
              value={params.cabinetscountersCost || ''}
              onChange={(e) => handleParamChange('cabinetscountersCost', e.target.value)}
              placeholder={getCapExSuggestion('cabinetscountersCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 20 yrs"
              value={params.cabinetscountersAge}
              onChange={(e) => handleParamChange('cabinetscountersAge', e.target.value)}
              width="80px"
            />
          </CapExRow>

          <CapExCategory>Exterior & Structure</CapExCategory>
          <CapExRow>
            <FormField
              label="Windows"
              prefix="$"
              value={params.windowsCost || ''}
              onChange={(e) => handleParamChange('windowsCost', e.target.value)}
              placeholder={getCapExSuggestion('windowsCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 50 yrs"
              value={params.windowsAge}
              onChange={(e) => handleParamChange('windowsAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Driveway/Parking"
              prefix="$"
              value={params.drivewayparkingLotCost || ''}
              onChange={(e) => handleParamChange('drivewayparkingLotCost', e.target.value)}
              placeholder={getCapExSuggestion('drivewayparkingLotCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 50 yrs"
              value={params.drivewayparkingLotAge}
              onChange={(e) => handleParamChange('drivewayparkingLotAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Landscaping"
              prefix="$"
              value={params.landscapingCost || ''}
              onChange={(e) => handleParamChange('landscapingCost', e.target.value)}
              placeholder={getCapExSuggestion('landscapingCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 10 yrs"
              value={params.landscapingAge}
              onChange={(e) => handleParamChange('landscapingAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Structure"
              prefix="$"
              value={params.structureCost || ''}
              onChange={(e) => handleParamChange('structureCost', e.target.value)}
              placeholder={getCapExSuggestion('structureCost', property.sqft)}
              width="120px"
            />
            <FormField
              label="Age"
              suffix="/ 50 yrs"
              value={params.structureAge}
              onChange={(e) => handleParamChange('structureAge', e.target.value)}
              width="80px"
            />
          </CapExRow>

          <CapExCategory>Other</CapExCategory>
          <CapExRow>
            <FormField
              label="Components"
              prefix="$"
              value={params.componentsCost || ''}
              onChange={(e) => handleParamChange('componentsCost', e.target.value)}
              placeholder={getCapExSuggestion('componentsCost', property.sqft)}
              width="120px"
              tooltip="Mechanical and electrical components like outlets, switches, fixtures, and minor systems."
            />
            <FormField
              label="Age"
              suffix="/ 10 yrs"
              value={params.componentsAge}
              onChange={(e) => handleParamChange('componentsAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
          <CapExRow>
            <FormField
              label="Other"
              prefix="$"
              value={params.otherCapexCost || ''}
              onChange={(e) => handleParamChange('otherCapexCost', e.target.value)}
              placeholder={getCapExSuggestion('otherCapexCost', property.sqft)}
              width="120px"
              tooltip="Any other capital items not listed: pool, sprinkler system, septic, well, fencing, deck, etc."
            />
            <FormField
              label="Age"
              suffix="yrs"
              value={params.otherCapexAge}
              onChange={(e) => handleParamChange('otherCapexAge', e.target.value)}
              width="80px"
            />
          </CapExRow>
        </InputGroup>

        <InputGroup title="Growth Assumptions">
          <FormField
            label="Rent Increase"
            suffix="%/yr"
            value={params.rentIncrease}
            onChange={(e) => handleParamChange('rentIncrease', e.target.value)}
            tooltip="Expected annual rent growth rate. Typically 2-4% to match inflation, but varies by market."
          />
          <FormField
            label="Expense Increase"
            suffix="%/yr"
            value={params.expenseIncrease}
            onChange={(e) => handleParamChange('expenseIncrease', e.target.value)}
            tooltip="Expected annual growth in operating expenses (insurance, utilities, maintenance). Typically 2-3%."
          />
          <FormField
            label="Tax Increase"
            suffix="%/yr"
            value={params.propertyTaxIncrease}
            onChange={(e) => handleParamChange('propertyTaxIncrease', e.target.value)}
            tooltip="Expected annual increase in property taxes. Many areas have caps (e.g., 2% in California), but can reassess on purchase."
          />
        </InputGroup>
      </AccordionSection>

      {/* Section 3: Valuation */}
      <AccordionSection
        stepNumber={3}
        title="Valuation Assumptions"
        summary={expandedSection !== 3 ? getValuationSummary() : null}
        isExpanded={expandedSection === 3}
        isCompleted={true}
        onToggle={() => setExpandedSection(expandedSection === 3 ? null : 3)}
      >
        <InputGroup title="Exit Strategy">
          <FormField
            label="Exit Cap Rate"
            suffix="%"
            value={valuation.exitCapRate}
            onChange={(e) => handleValuationChange('exitCapRate', e.target.value)}
            tooltip="Capitalization rate used to estimate future sale price. Sale Price = NOI ÷ Cap Rate. Higher cap rate = lower sale price."
            error={fieldErrors.exitCapRate}
          />
          <FormField
            label="Hold Period"
            suffix="years"
            value={valuation.holdPeriod}
            onChange={(e) => handleValuationChange('holdPeriod', e.target.value)}
            tooltip="Number of years you plan to own the property before selling. Affects IRR calculation and total returns."
          />
        </InputGroup>
      </AccordionSection>

      {/* Validation Errors Display */}
      {validationErrors.length > 0 && (
        <ErrorList>
          {validationErrors.map((error, index) => (
            <ErrorItem key={index}>{error}</ErrorItem>
          ))}
        </ErrorList>
      )}

      {/* Action Button - Changes based on current section */}
      {/* Hide button when results exist and we're viewing results or no section is expanded */}
      {!(results && (expandedSection === 'results' || expandedSection === null)) && (
        <RunAnalysisButton 
          data-run-analysis
          onClick={() => {
            if (expandedSection === 3 || results) {
              runAnalysis();
            } else {
              // Advance to next section
              const nextSection = expandedSection === null ? 1 : expandedSection + 1;
              setExpandedSection(nextSection);
            }
          }}
          disabled={(expandedSection === 3 && (property.purchasePrice <= 0 || property.unit1Rent <= 0 || isLoading)) || (expandedSection === 1 && (property.purchasePrice <= 0 || property.unit1Rent <= 0))}
        >
          {isLoading ? 'Calculating...' : (expandedSection === 3 || results) ? (
            <>
              <IoCalculator size={20} />
              {results ? 'Update Analysis' : 'Run Analysis'}
            </>
          ) : (
            <>
              Next Step
              <IoArrowForward size={20} />
            </>
          )}
        </RunAnalysisButton>
      )}

      {/* Results Section */}
      {results && (
        <AccordionSection
          stepNumber="✓"
          title="Analysis Results"
          isExpanded={expandedSection === 'results'}
          isCompleted={true}
          onToggle={() => setExpandedSection(expandedSection === 'results' ? null : 'results')}
        >
          {/* Summary Cards */}
          <SummaryCards>
            <SummaryCard>
              <SummaryIcon><IoCash size={20} /></SummaryIcon>
              <SummaryValue>
                {formatCurrency(property.purchasePrice * (1 - params.loanAmount / 100) + property.purchasePrice * 0.03)}
              </SummaryValue>
              <SummaryLabel>Cash Invested</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryIcon><IoTrendingUp size={20} /></SummaryIcon>
              <SummaryValue $positive={results.coc?.[0]?.COC > 0}>
                {results.coc?.[0]?.COC !== null && results.coc?.[0]?.COC !== undefined 
                  ? `${results.coc[0].COC}%` 
                  : 'N/A'}
              </SummaryValue>
              <SummaryLabel>Year 1 COC</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryIcon><IoWallet size={20} /></SummaryIcon>
              <SummaryValue $positive={results.cashFlow?.[0]?.["Cash Flow"] > 0}>
                {formatCurrency(results.cashFlow?.[0]?.["Cash Flow"] / 12)}
              </SummaryValue>
              <SummaryLabel>Monthly Cash Flow</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryIcon><IoStatsChart size={20} /></SummaryIcon>
              <SummaryValue $positive={results.annual_irrs?.[4]?.IRR > 0}>
                {(() => {
                  const irrData = results.annual_irrs?.[4];
                  if (!irrData || irrData.IRR === null || irrData.IRR === undefined) return 'N/A';
                  const isFallback = irrData.TotalReturnDollar !== null && irrData.TotalReturnDollar !== undefined;
                  if (isFallback) {
                    return `${irrData.IRR}%/${irrData.TotalReturnDollar < 0 ? '-' : ''}$${Math.abs(irrData.TotalReturnDollar).toLocaleString()}`;
                  }
                  return `${irrData.IRR}%`;
                })()}
              </SummaryValue>
              <SummaryLabel>{valuation.holdPeriod}-Year IRR</SummaryLabel>
            </SummaryCard>
          </SummaryCards>

          {/* Tabs and View Toggle */}
          <TabsRow>
            <TabsContainer>
              <Tab $active={activeTab === 'cashflow'} onClick={() => setActiveTab('cashflow')}>Cash Flow</Tab>
              <Tab $active={activeTab === 'amortization'} onClick={() => setActiveTab('amortization')}>Amortization</Tab>
              <Tab $active={activeTab === 'irr'} onClick={() => setActiveTab('irr')}>IRR</Tab>
              <Tab $active={activeTab === 'coc'} onClick={() => setActiveTab('coc')}>COC</Tab>
              <Tab $active={activeTab === 'valuation'} onClick={() => setActiveTab('valuation')}>Valuation</Tab>
            </TabsContainer>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {activeTab === 'cashflow' && viewMode === 'table' && (
                <DetailsToggle 
                  $active={showDetails} 
                  onClick={() => setShowDetails(!showDetails)}
                  title="Show/hide detailed columns"
                >
                  <IoListOutline /> {showDetails ? 'Less' : 'More'}
                </DetailsToggle>
              )}
              <ViewToggle>
                <ViewButton 
                  $active={viewMode === 'table'} 
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  <IoGridOutline />
                </ViewButton>
                <ViewButton 
                  $active={viewMode === 'chart'} 
                  onClick={() => setViewMode('chart')}
                  title="Chart View"
                >
                  <IoBarChartOutline />
                </ViewButton>
              </ViewToggle>
              <div style={{ position: 'relative' }}>
                <ExportButton onClick={() => setShowExportDropdown(!showExportDropdown)}>
                  <IoDownloadOutline /> Export
                </ExportButton>
                {showExportDropdown && (
                  <ExportDropdown>
                    <ExportCheckbox>
                      <input 
                        id="export-include-inputs"
                        name="export-include-inputs"
                        type="checkbox" 
                        checked={exportIncludeInputs} 
                        onChange={(e) => setExportIncludeInputs(e.target.checked)} 
                      />
                      <label htmlFor="export-include-inputs">Include Input Data</label>
                    </ExportCheckbox>
                    <ExportConfirmButton onClick={handleExport}>
                      Download Excel
                    </ExportConfirmButton>
                  </ExportDropdown>
                )}
              </div>
            </div>
          </TabsRow>

          {/* Tab-specific explanations */}
          <TabExplanation>
            {activeTab === 'cashflow' && (
              <>
                <strong>Cash Flow</strong> shows your annual profit after all expenses and debt service. We calculate it as: <strong>Effective Gross Revenue</strong> (rent minus vacancy) − <strong>Operating Expenses</strong> (taxes, insurance, management, utilities, CapEx reserves) − <strong>Debt Service</strong> (principal + interest payments). Positive cash flow means the property generates income; negative means you're subsidizing it.
              </>
            )}
            {activeTab === 'amortization' && (
              <>
                <strong>Amortization</strong> shows how your loan balance decreases over time. Each payment splits between principal (building equity) and interest (cost of borrowing). Early in the loan, most goes to interest; over time, more goes to principal. This table helps you see your equity position at any point during the hold period.
              </>
            )}
            {activeTab === 'irr' && (
              <>
                <strong>Internal Rate of Return (IRR)</strong> measures your total annualized return including cash flow, principal paydown, and sale proceeds. It answers: "What annual return did this investment generate?" Early years may show negative IRR because selling costs and transaction fees haven't been offset by gains yet—this is normal and doesn't mean the investment is bad. IRR becomes more meaningful over longer hold periods.
              </>
            )}
            {activeTab === 'coc' && (
              <>
                <strong>Cash-on-Cash Return (COC)</strong> measures annual cash flow as a percentage of your initial cash investment (down payment + closing costs). Unlike IRR, it doesn't consider appreciation or equity buildup—just pure cash returns. A 10% COC means for every $10,000 invested, you receive $1,000 in annual cash flow. Great for comparing properties or alternative investments.
              </>
            )}
            {activeTab === 'valuation' && (
              <>
                <strong>Valuation</strong> estimates the property's future sale price using the <strong>Exit Cap Rate</strong> method: Sale Price = Year's NOI ÷ Cap Rate. A lower cap rate means higher valuation (and vice versa). This projection helps you estimate total returns including appreciation. The remaining loan balance shows how much equity you'd walk away with at sale.
              </>
            )}
          </TabExplanation>

          {renderResultsTable()}

          <p style={{ fontSize: '13px', color: '#666', marginTop: '16px', textAlign: 'center' }}>
            Adjust any section above and re-run to update results
          </p>
        </AccordionSection>
      )}

      {/* Query Limit Modal */}
      {showQueryLimitModal && (
        <ModalBackgroundStyled onClick={() => setShowQueryLimitModal(false)}>
          <QueryLimitModalContent onClick={(e) => e.stopPropagation()}>
            <QueryLimitTitle>
              {noSubscription ? 'Free Queries Used Up' : 'No Queries Remaining'}
            </QueryLimitTitle>
            <QueryLimitMessage>
              {noSubscription ? (
                <>
                  You've used all 5 free analyses this month. Upgrade to keep analyzing, or wait for your monthly reset.
                  {freeResetDate && (
                    <>
                      <br /><br />
                      <strong>Your free queries reset on {new Date(freeResetDate).toLocaleDateString('en-US')}</strong>
                    </>
                  )}
                </>
              ) : (
                <>
                  You've used all your analysis queries for this billing period.
                  {getResetInfo() && (
                    <>
                      <br /><br />
                      <strong>Your plan resets on {getResetInfo().date}</strong> with {getResetInfo().queries} queries.
                    </>
                  )}
                </>
              )}
            </QueryLimitMessage>
            <QueryLimitButtonGroup>
              <QueryLimitButton onClick={() => setShowQueryLimitModal(false)}>
                {noSubscription ? 'Maybe Later' : 'Wait for Reset'}
              </QueryLimitButton>
              <QueryLimitButton primary onClick={async () => {
                try {
                  if (noSubscription) {
                    // No subscription - go to signup to choose a plan
                    setShowQueryLimitModal(false);
                    navigate('/signup');
                  } else {
                    // Has subscription - open billing portal to upgrade
                    const cust = await getCustomer(user.uid);
                    console.log('Customer data:', cust);
                    if (cust?.stripeId) {
                      await manageSubscription(cust.stripeId);
                    } else {
                      console.error('No stripeId found:', cust);
                      alert('Unable to load billing portal. Please try again or go to Profile to manage your subscription.');
                    }
                  }
                } catch (err) {
                  console.error('Error:', err);
                  alert('An error occurred. Please try again.');
                }
                setShowQueryLimitModal(false);
              }}>
                {noSubscription ? 'Choose a Plan' : 'Upgrade Plan'}
              </QueryLimitButton>
            </QueryLimitButtonGroup>
          </QueryLimitModalContent>
        </ModalBackgroundStyled>
      )}
    </PageContainer>
  );
};

export default ManualModePage;
